/* eslint-disable @typescript-eslint/no-use-before-define */

import { IValidationErrorDetail } from './_api';

/* eslint-disable no-use-before-define */
export abstract class ApiErrorBase<TAppCode extends string = string> extends Error {
  constructor(public appCode: TAppCode, message: string, public developerMessage?: string) {
    super();
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'Api Error';
    this.message = message;
    // eslint-disable-next-line dot-notation
    if (Error['captureStackTrace']) {
      // eslint-disable-next-line dot-notation
      Error['captureStackTrace'](this, this.constructor);
    }
  }
}

export type ServerErrors<TAppErrors> = ApiErrorBase<any> | TAppErrors;

interface IErrorRegistration<TError extends { appCode: string }, TData> {
  appCode: string;
  status: number;
  toJson: (e: TError) => TData;
  fromJson: (d: TData) => TError;
}

const UnknownServerErrorAppCode = 'UnknownServerError';

export class ApiError {
  static errors: IErrorRegistration<any, any>[] = [];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  static registerCore() {}

  static isApiError(testError: any): testError is ApiErrorBase<any> {
    return testError?.appCode;
  }

  static register<TErrorConstructor extends new (...args: any[]) => { appCode: string; developerMessage?: string }>(
    _error: TErrorConstructor,
    status: number
  ) {
    return {
      transit<TKeys extends keyof Omit<InstanceType<TErrorConstructor>, 'developerMessage' | 'appCode'>>(
        ...keys: TKeys[]
      ) {
        return {
          recreate(
            fromJson: (d: Pick<InstanceType<TErrorConstructor>, TKeys>) => InstanceType<TErrorConstructor>
          ): ClassDecorator {
            ApiError.errors.push({
              appCode: new _error().appCode,
              status,
              toJson: e =>
                keys.reduce((m, c) => {
                  // eslint-disable-next-line no-param-reassign
                  m[c] = e[c];
                  return m;
                }, {} as any),
              fromJson,
            });
            return () => {};
          },
        };
      },
    };
  }

  static createApiError(data: any) {
    const appCode = data?.appCode;
    if (appCode) {
      const errorReg = ApiError.errors.find(e => e.appCode === appCode);
      if (errorReg) {
        return errorReg.fromJson(data);
      }
    }
    return new UnknownServerError(data);
  }
}

@ApiError.register(UnknownServerError, 500)
  .transit('data')
  .recreate(d => new UnknownServerError(d.data))
export class UnknownServerError extends ApiErrorBase<typeof UnknownServerErrorAppCode> {
  constructor(public data: any, developerMessage?: string) {
    super(UnknownServerErrorAppCode, 'Unknown Server Error', developerMessage);
  }
}

export function getErrorResponse<TAppCode extends string>(
  error: any
): { status: number; send: { appCode: TAppCode; [key: string]: any }; developerMessage?: string } {
  if (ApiError.isApiError(error)) {
    const errorReg = ApiError.errors.find(e => e.appCode === error.appCode);
    if (errorReg) {
      return {
        status: errorReg.status,
        send: { ...errorReg.toJson(error), appCode: error.appCode },
        developerMessage: error.developerMessage,
      };
    }
  }

  return { status: 500, send: { message: 'Unknown Server Error', appCode: UnknownServerErrorAppCode as TAppCode } };
}

@ApiError.register(RequestEntityTooLargeError, 413)
  .transit('message', 'expected', 'length', 'limit')
  .recreate(d => new RequestEntityTooLargeError(d.message, d.expected, d.length, d.limit))
export class RequestEntityTooLargeError extends ApiErrorBase<'RequestEntityTooLargeError'> {
  constructor(message: string, public expected: number, public length: number, public limit: number) {
    super('RequestEntityTooLargeError', message);
  }
}

@ApiError.register(UnauthorisedError, 401)
  .transit('message')
  .recreate(d => new UnauthorisedError(d.message))
export class UnauthorisedError extends ApiErrorBase<'UnauthorisedError'> {
  constructor(message: string, developerMessage?: string) {
    super('UnauthorisedError', message, developerMessage);
  }
}

@ApiError.register(BadRequestError, 400)
  .transit('message')
  .recreate(d => new BadRequestError(d.message))
export class BadRequestError extends ApiErrorBase<'BadRequestError'> {
  constructor(message: string, developerMessage?: string) {
    super('BadRequestError', message, developerMessage);
  }
}

@ApiError.register(NotFoundError, 404)
  .transit('message')
  .recreate(d => new NotFoundError(d.message))
export class NotFoundError extends ApiErrorBase<'NotFoundError'> {
  constructor(message: string, developerMessage?: string) {
    super('NotFoundError', message, developerMessage);
  }
}

@ApiError.register(ForbiddenError, 403)
  .transit('message')
  .recreate(d => new ForbiddenError(d.message))
export class ForbiddenError extends ApiErrorBase<'ForbiddenError'> {
  constructor(message: string, developerMessage?: string) {
    super('ForbiddenError', message, developerMessage);
  }
}

@ApiError.register(ValidationError, 422)
  .transit('errors')
  .recreate(d => new ValidationError(d.errors))
export class ValidationError extends ApiErrorBase<'ValidationError'> {
  constructor(public errors: IValidationErrorDetail[], developerMessage?: string) {
    super('ValidationError', "Validation error(s)", developerMessage);
  }
}

@ApiError.register(ArgumentError, 500)
  .transit('parameter')
  .recreate(d => new ArgumentError(d.parameter))
export class ArgumentError extends ApiErrorBase<'ArgumentError'> {
  constructor(public parameter: string, developerMessage?: string) {
    super('ArgumentError', `Argument '${parameter}' was not defined`, developerMessage);
  }
}

@ApiError.register(ConfigurationError, 500)
  .transit('message')
  .recreate(d => new ConfigurationError(d.message))
export class ConfigurationError extends ApiErrorBase<'ConfigurationError'> {
  constructor(message: string, developerMessage?: string) {
    super('ConfigurationError', message, developerMessage);
  }
}

@ApiError.register(ConflictError, 409)
  .transit('message')
  .recreate(d => new ConflictError(d.message))
export class ConflictError extends ApiErrorBase<'ConflictError'> {
  constructor(message: string, developerMessage?: string) {
    super('ConflictError', message, developerMessage);
  }
}
